export enum ShowProgressBar {
  BOTTOM = 'bottom',
  ABOVEHEADER = 'aboveheader',
  BELOWHEADER = 'belowheader',
  TOPBOTTOM = 'topbottom',
}

export enum ShowNavigationButtons {
  TOP = 'top',
  BOTTOM = 'bottom',
  BOTH = 'both',
}

export enum CellType {
  CHECKBOX = 'checkbox',
  RADIOGROUP = 'radiogroup',
  TAGBOX = 'tagbox',
  TEXT = 'text',
  COMMENT = 'comment',
  BOOLEAN = 'boolean',
  RATING = 'rating',
  DEFAULT = 'default',
}

export enum ChoicesOrder {
  ASC = 'asc',
  DESC = 'desc',
  RANDOM = 'random',
}

export interface IFormSchema {
  title: string;
  description?: string;
  logo?: string;
  logoWidth?: string;
  logoHeight?: string;
  logoFit?: string;
  firstPageIsStarted?: boolean;
  completedHtml?: string;
  completeText?: string;
  pageNextText?: string;
  pagePrevText?: string;
  showProgressBar?: ShowProgressBar | string;
  showNavigationButtons?: ShowNavigationButtons | string;
  pages: IFormPage[];
}

export interface IFormPage {
  name: string;
  title: string;
  description: string;
  elements: IFormElement[];
}

export interface IFormElement {
  type: string;
  name: string;
  title: string;
  description?: string;
  isRequired?: boolean;
  autocomplete?: string;
  visible?: boolean;
  visibleIf?: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  inputType?: string;
  readOnly?: boolean;
  rows?: number | string[] | IRowValueText[];
  columns?: string[] | IColumnValueText[] | IColumnNameTitle[];
  choices?: string[] | number[] | IChoiceValueText[] | IChoiceWithImage[];
  imageHeight?: number;
  maxSize?: number;
  allowMultiple?: boolean;
  acceptedTypes?: string;
  showOtherItem?: boolean;
  otherText?: string;
  otherPlaceholder?: string;
  labelTrue?: string;
  labelFalse?: string;
  defaultValue?: any;
  rateType?: string;
  rateCount?: number;
  rateMin?: number;
  rateMax?: number;
  rateStep?: number;
  displayRateDescriptionsAsExtremeItems?: boolean;
  minRowCount?: number;
  maxRowCount?: number;
  rowCount?: number;
  allowAddRows?: boolean;
  allowRemoveRows?: boolean;
  allowRowsDragAndDrop?: boolean;
  addRowText?: string;
  removeRowText?: string;
  showCommentArea?: boolean;
  commentText?: string;
  commentPlaceholder?: string;
  imageFit?: string;
  multiSelect?: boolean;
  showLabel?: boolean;
  min?: string | number;
  max?: string | number;
  step?: number;
  choicesOrder?: ChoicesOrder;
  contentMode?: 'video';
  swapOrder?: boolean;
  minRateDescription?: string;
  maxRateDescription?: string;
  cellType?: CellType;
  isAllRowRequired?: boolean;
  eachRowUnique?: boolean;
  showSelectAllItem?: boolean;
  showNoneItem?: boolean;
  noneText?: string;
  selectAllText?: string;
  hideIfRowsEmpty?: boolean;
  rowsOrder?: 'random';
  items?: IMultipleTextItems[];
  expression?: string;
  displayStyle?: string;
  defaultValueExpression?: string;
  html?: string;
  elements?: IFormElement[];
}

export interface IRowValueText {
  value: string;
  text: string;
}

export interface IColumnValueText {
  value: string;
  text: string;
}

export interface IColumnNameTitle {
  name: string;
  title?: string;
}

export interface IChoiceValueText {
  value: string;
  text: string;
}

export interface IChoiceWithImage {
  value: string;
  imageLink: string;
  text?: string;
}

export interface IMultipleTextItems {
  name: string;
  title?: string;
  placeholder?: string;
  inputType?: string;
  isRequired?: boolean;
  maxLength?: number;
}

export interface IUserInfo {
  cidade?: string;
  numero?: string;
  preferred_username?: string;
  cep?: string;
  uf?: string;
  complemento?: string;
  cpf?: string;
  celular?: string;
  dataNascimento?: string;
  email?: string;
  nomeCompleto?: string;
  bairro?: string;
  given_name?: string;
  logradouro?: string;
  name?: string;
  tipoLogradouro?: string;
  family_name?: string;
  nomeMae?: string;
  [key: string]: unknown;
}

export interface IFormData {
  id: string;
  title: string;
  description: string;
  schema: IFormSchema;
  callbackURL: string;
  createdAt: string;
  updatedAt: string;
}

export interface IFormResponse<T> {
  success: boolean;
  data: T;
  message: string;
  meta: Record<string, number> | null;
}

export interface IFormSubmitPayload {
  userId: string;
  answers: Record<string, any>;
  organId: string;
  serviceId: string;
}

export interface IFormSubmitResult {
  userId: string | null;
  answers: Record<string, any>;
  organId: string | null;
  serviceId: string | null;
  form: IFormData;
  id: string;
  submittedAt: string;
}

export interface DynamicFormConfig {
  apiBaseUrl: string;
}

export interface DynamicFormIdentifiers {
  formId: string;
  organId?: string;
  serviceId?: string;
}

export interface DynamicFormCallbacks {
  onSuccess: (result: IFormSubmitResult) => void;
  onError: (error: unknown) => void;
  onClose?: () => void;
  onSubmitStart?: () => void;
}

export interface DynamicFormProps {
  formId: string;
  apiBaseUrl: string;
  accessToken: string;
  userInfo?: IUserInfo;
  identifiers?: Record<string, any>;
  config?: Partial<{
    callbackUrl?: string;
    loadingText?: string;
    successTitle?: string;
    successMessage?: string;
    showHeader?: boolean;
    showProgressBar?: boolean;
    showNavigation?: boolean;
    prevButtonText?: string;
    nextButtonText?: string;
    submitButtonText?: string;
  }>;
  callbacks?: Partial<DynamicFormCallbacks> & {
    onFormLoad?: (schema: IFormSchema) => void;
    onValidationError?: (errors: any) => void;
    onPageChange?: (newPage: number, oldPage: number) => void;
  };
  theme?: Partial<IDynamicFormTheme>;
  className?: string;
}

export interface IDynamicFormTheme {
  headerBackcolor: string;
  formBackcolor: string;
  primaryColor: string;
  primaryColorDark: string;
  primaryColorLight: string;
  textPrimary: string;
  textSecondary: string;
  errorColor: string;
  successColor: string;
  fontFamily: string;
}

export type FormState = 'loading' | 'form' | 'success' | 'error';

export interface IInitialValues {
  [key: string]: string;
}

export interface IFormResponseData {
  [fieldName: string]: any;
}
